import { BeamManager } from "../beam/beam-managers"
import { BeamFlexure } from '../beam/beam-model.js'
import { BeamFlexureDesign } from '../design/beam-flexure-design.js'
import { DesignConfigurator } from "../design/design-configurator.js"
import { showSmallLengthWithUnits } from 'ws-units'
import { lrfdCombinations, serviceCases, noCombinations } from "../load-combinations/load-combination.js"

export function makeVisitorBeam() {
    var beam = new BeamFlexure();
    var design = new BeamFlexureDesign();
    design.designType = 'LRFD';
    design.loadCombination = lrfdCombinations
    design.loadCases = noCombinations
    design.serviceCases = serviceCases

    var beamTitle = "Untitled Beam"
    var designConfigurator = new DesignConfigurator()
    const project = new BeamProject(
      beamTitle, beam, design, designConfigurator.shape, designConfigurator.material);
    project.isActive = true;
    const isLoggedIn = false 
    BeamManager.makeDefaultBeam(project, isLoggedIn)
    return project
}


export class BeamProject {
    constructor(title, beam, design, shape, material) {
        this.title = title;
        this.isActive = false;
        this.isModified = false;
        this.beam = beam;
        this.design = design;

        if(this.design) {
            sanitizeAllLoadCaseTypes(this)
        } 

        // remove speical character '/' from shape keys...firebase datastore
        // can't serialize that character.
        var newShape = {};
        if(typeof shape !== 'undefined' && shape !== null) {
            Object.keys(shape).forEach(function(key) {
                var value = shape[key];
                var newKey = key.replace("/", "_over_");
                newShape[newKey] = value;
            });
        }

        this.shape = newShape;
        this.material = material;

        this.tags = []; // array of strings
        this.createdAt = Date.now();
        this.modifiedAt = Date.now();
        this.lastSavedAt = Date.now();
        this.key = null;
        //custom shape Support
        this.customShape = {};

        // wood plys
        this.numberOfPlys = 1;

        // load precision - TODO: change this to persist load display (lbs vs kips vs N vs kN)
        this.loadPrecision = 2;

    }

    static load(json) {
        var p = Object.assign(new BeamProject, json);
        p.beam = BeamFlexure.load(json.beam);
        p.design = BeamFlexureDesign.load(json.design)
        // 7/24/22 Sanitize our load cases, service cases and load combination
        // This work is a refactor to help track down an issue reported by Mark Fiskin 
        // where load cases (other than D) are empty. As part of the work we are removing
        // the ugly to pretty and pretty to ugly mappings. 
        // Root Cause: load cases "exclude" flag was being copied when creating a copy of 
        // a project where load cases had been excluded via the UI. 
        // Solution: stop persisting "exclude" state on all load cases. customers can still
        // exclude them via the UI but the selections will not be saved.
        // NOTE: If there are other issues, related to the refactor, not persisting, 
        // not copying or ignoring the "exlcude" flag should also work.
        try {
            sanitizeAllLoadCaseTypes(p)
        } catch(error) {
            console.log(error)
            console.log(p)
        }

        p.isModified = false;

            return p;
    }

    serialize() {
        var newShape = {};
        if(typeof this.shape !== 'undefined' && this.shape !== null) {
            Object.keys(this.shape).forEach(function(key) {
                var value = this.shape[key];
                var newKey = key.replace("/", "_over_");
                newShape[newKey] = value;
            }.bind(this));
        }
        this.shape = newShape;

        if(this.beamController) {
            delete this.beamController
        }
        return this
    }

    static createCopy(json) {
        var p = Object.assign(new BeamProject, json);
        p.title = "Copy of " + p.title;
        p.beam = BeamFlexure.load(json.beam);
        p.design = BeamFlexureDesign.load(json.design)

        try {
            sanitizeAllLoadCaseTypes(p)
        } catch(error) {
            console.log(error)
            console.log(p)
        }

        p.isModified = false;
        p.tags = []; // array of strings
        p.createdAt = Date.now();
        p.modifiedAt = Date.now();
        p.lastSavedAt = Date.now();
        p.key = null;
        p.isActive = true
        return p;
    }

    static makeSimpleNewProject() {
        let beam = new BeamFlexure()
        let design = new BeamFlexureDesign()
        design.designType = 'LRFD'
        design.loadCombination = lrfdCombinations
        design.loadCases = noCombinations
        design.serviceCases = serviceCases
    
        let beamTitle = "Untitled Beam"
        let designConfigurator = new DesignConfigurator() // temporary
        let project = new BeamProject(
            beamTitle, beam, design, designConfigurator.shape, designConfigurator.material)
        project.isActive = true
        BeamManager.makeNewSimpleBeam(project)
        return project
    }

    get isNewProject() {
        return this.key === null;
    }

    get shapeName() {
        if(this.isNorthAmericanSteel) {
            return this.shape['AISC_Manual_Label']
        }
        if('Profile' in this.shape) {
            return this.shape['Profile'];
        }
        if(this.isEngineeredLumber) {
            let str = this.shape['Type'] + " " + this.shape['Grade'] + " " + this.shape['Width'] + " &times; " + this.shape['Height']
            if(this.shape.flitch) {
                str += ` Flitch`
            }
            return str
        }
        if(this.isWood) {
            let str = this.shape['Name']
            if(this.shape.flitch) {
                str += ` Flitch`
            }
            return str
        }
        return this.shape['Name'];
    }

    get shapeIx() {
        let ix = parseFloat(this.shape.Ix)
        if(this.isEuropeanSteel) {
            // convert to internal units: m^4 -> in^4 (2402509.61)
            return ix * Math.pow(39.3701, 4) 
        }

        if(this.isEngineeredLumber || this.isWood) {
            ix *= parseFloat(this.numberOfPlys)
            if(this.shape.flitch) {
                ix += this.transformedFlitchIx
            }
        } 
        return ix
    }

    get shapeIxString() {
        const ix = this.shapeIx
        if(this.isEuropeanSteel) {
            // convert to centimeters / common display units
            return parseFloat((ix / Math.pow(39.3701, 4) * Math.pow(100,4)).toFixed(3)).toString() + " cm<sup>4</sup>";
        } 

        return parseFloat(ix.toFixed(3).toString()) + " in<sup>4</sup>";
    }

    get shapeIy() {
        if(this.isWood) {
            let b = parseFloat(this.shape.d)
            let h = parseFloat(this.shape.b) * this.numberOfPlys
            let iy = b * h * h * h / 12
            if(this.shape.flitch) {
                iy += this.transformedFlitchIy
            }
            return iy
        }

        if(this.isEuropeanSteel) {
            // convert to internal units: m^4 -> in^4 (2402509.61)
            return parseFloat(this.shape.Iy) * Math.pow(39.3701, 4) 
        }

        if(this.isNorthAmericanSteel) {
            return parseFloat(this.shape.Iy)
        }

        throw new Error(`Iy does not exist for this shape: ${this}`)
    }

    get shapeIyString() {

        let iy = this.shapeIy

        if(this.isEuropeanSteel) {
            // convert to centimeters / common display units
            return parseFloat((iy / Math.pow(39.3701, 4) * Math.pow(100,4)).toFixed(3)).toString() + " cm<sup>4</sup>";
        }

        return parseFloat(iy.toFixed(3).toString()) + " in<sup>4</sup>";
    }

    get flitchTransformFactor() {
        const Es = 29e6
        // if engineering wood we need to multiply by...1000
        let Ew = parseFloat(this.material.E)
        if(this.isEngineeredLumber) {
            Ew *= 1e3
        }
        return Es / Ew
    }

    get transformedFlitchArea() {
        const thickness = this.shape.flitch.plateThickness
        const height = this.shape.flitch.plateHeight
        const nPlates = this.shape.flitch.numberOfPlates
        const n = this.flitchTransformFactor
        return n * nPlates * thickness * height
    }

    get flitchWeight() {
        const thickness = this.shape.flitch.plateThickness
        const height = this.shape.flitch.plateHeight
        const nPlates = this.shape.flitch.numberOfPlates
        const steelDensity = 0.284 // lb / in^3
        return steelDensity * thickness * height * 12.0 * nPlates // lb/ft (pounds per foot: same as steel shapes) 
    }

    get transformedFlitchIx() {
        const thickness = this.shape.flitch.plateThickness
        const height = this.shape.flitch.plateHeight
        const nPlates = this.shape.flitch.numberOfPlates
        const n = this.flitchTransformFactor
        const Ipx = n * nPlates * thickness * height * height * height / 12.0
        return Ipx 
    }

    get transformedFlitchSx() {
        const height = this.shape.flitch.plateHeight
        return this.transformedFlitchIx * 2.0 / height
    }

    get transformedFlitchIy() {
        const thickness = this.shape.flitch.plateThickness
        const height = this.shape.flitch.plateHeight
        const nPlates = this.shape.flitch.numberOfPlates
        const n = this.flitchTransformFactor
        const Ipy = n * nPlates * thickness * thickness * thickness * height / 12.0
        return Ipy 
    }

    /**
     * Return internal units in lbs/ft
     * */
    get shapeWeight() {
        let w = parseFloat(this.shape.W)
        if(this.isNorthAmericanSteel) {
            return w
        }
        if(this.isWood) {
            var gamma = 0.25; //1.1 slugs/ft^3/12in/12in*32.2
            w = parseFloat(gamma * parseFloat(this.shape.A))
            this.shape.W = w
            w *= parseFloat(this.numberOfPlys)
            if(this.shape.flitch) {
                w += this.flitchWeight
            }
            return w
        }

        // so far it seems like only lumber does not contain a weight
        if(!("W" in this.shape)) {
            w = parseFloat(parseFloat(this.shape.A) * parseFloat(this.material.Density) * 12.0)
            this.shape.W = w
        }

        if(this.isEngineeredLumber) {
            w *= parseFloat(this.numberOfPlys)
            if(this.shape.flitch) {
                w += this.flitchWeight
            }
            return w
        }

        if(this.isEuropeanSteel) {
            //convert to internal units
            return w * 0.671968154 // kg/m -> lb/ft
        }
        return w
    }

    get shapeWeightString() {
        let w = this.shapeWeight

        if(this.isEuropeanSteel) {
            // convert to kg / m common display units
            return parseFloat(w / 0.671968154).toString() + " kg/m";
        }

        return parseFloat(w.toFixed(2)).toString() + " lbs/ft";
    }

    get shapeCategory() {
        if (this.isNorthAmericanSteel) {
            return 'north-american-steel';
        }
        if (this.isWood) {
            return 'dimensional-lumber';
        }
        if (this.isEngineeredLumber) {
            return 'engineered-wood';
        }
        if (this.isEuropeanSteel) {
            return 'european-steel';
        }
        if (this.isCustom) {
            return 'custom';
        }
        return 'unknown';
    }

    get shapeSubCategory() {
        if (this.isNorthAmericanSteel || this.isEuropeanSteel || this.isEngineeredLumber) {
            return this.shape['Type'];
        }
        return undefined
    }

    get isNorthAmericanSteel() {
        return this.material && 'Type' in this.material && this.material['Type'] === 'AISC Steel'
    }

    get isWood() {
        return this.material && 'Type' in this.material && this.material['Type'] === "Visually Graded Lumber"
    }

    get isEngineeredLumber() {
        return this.shape && 'Grade' in this.shape
    }

    get isEuropeanSteel() {
        return this.material && 'Type' in this.material && this.material['Type'] === "European Steel"
    }

    get isCustom() {
        return this.material && 'Type' in this.material && this.material['Type'] === "Custom"
    }

    get isAluminum() {
        return this.material && 'Type' in this.material && this.material['Type'] === "Aluminum"
    }

    get isASTMSteel() {
        return this.material && 'Type' in this.material && this.material['Type'] === "ASTM Steel"
    }

    get isLRFD() {
        return this.design.designType === 'LRFD';
    }

    get isASD() {
        return this.design.designType === 'ASD';
    }

    get isNoDesign() {
        return this.design.designType === 'None';
    }

    get isSteelShape() {
        console.log('fix me')
        return ('AISC_Manual_Label' in shapeData || 'Profile' in shapeData || shapeData['Type'] === 'Dayton Superior Soldier')
    }

    get isFormTechShape() {
        return this.shape['Type'] == 'Custom Aluminum' || this.shape['Type'] == 'Dayton Superior Soldier';
    }

    get isSteelTee() {
        return this.isNorthAmericanSteel && (this.shape['Type'] === "WT" || this.shape['Type'] === "MT" || this.shape['Type'] === "ST")
    }

    get isHssRectangle() {
        return this.isNorthAmericanSteel && this.shape['Type'] === "HSS Rectangle"
    }

    get isHssRound() {
        return 'Type' in this.shape && this.shape['Type'] === 'HSS Round'
    }
      
    get isPipe() {
        return 'Type' in this.shape && this.shape['Type'] === 'PIPE'
    }

    get shapeCanBeRotated() {
        return this.isHssRectangle
    }

    get materialName() {
        switch(this.material['Type']) {
            case "Visually Graded Lumber":
                return this.material['Species'];
            case "European Steel":
                return "Steel " + this.material['Name'];
            case "AISC Steel":
                return "Steel " + this.material['Name'];
            case "Custom":
                return this.material['Name'];
            case "TimberStrand LSL":
            case "Microllam LVL":
            case "Parallam PSL":
            case "LVL (East)":
            case "LVL (West)":
            case "Stud LVL (East)":
            case "Glulam 24F-V4 IJC":
                return this.material['Type'] + " " + this.material['Grade'];
            default:
                return this.material['Name'];
        }
    }

    get materialGradeName() {
        switch(this.material['Type']) {
            case "Visually Graded Lumber":
            case "TimberStrand LSL":
            case "Microllam LVL":
            case "Parallam PSL":
            case "LVL (East)":
            case "LVL (West)":
            case "Stud LVL (East)":
                return this.material['Grade']
            default:
                return ''
        }
    }

    get designTypeStr() {
        if(this.isNoDesign) {
            return "Basic Design";
        }

        var str = "";
        if(this.isNorthAmericanSteel || this.isWood) {
            str = this.material['Type'];
        }

        // 2/6/2022 customer request: add the design spec.
        if(str === 'AISC Steel') {
            str = 'AISC 360-16'
        }

        if(str === 'Visually Graded Lumber') {
            str = 'NDS 2018'
        }

        if(this.isEngineeredLumber) {
            str = "Engineered Lumber";
        }

        str += " " + this.design.designType;

        if(this.design.loadCombination !== null && this.design.loadCombination.name === 'No Combos') {
            str += ' No Combos';
        }
        return str;
    }

    get spanStr() {
        return (this.beam.spans.length === 1) ? "1 Span" : this.beam.spans.length + " Spans"
    }

    get loadStr() {
        var nLoads = this.beam.totalNumberOfLoads;
        return (nLoads === 1) ? "1 Load" : nLoads + " Loads"
    }

    get bracingStr() {
        let html = 'Continuous Bracing';
        if (this.design.bracingType == 'continuous') {
            html = 'Continuous Bracing';
        } else if (this.design.bracingType == 'equal') {
            html = 'Braced @ ' + showSmallLengthWithUnits(this.design.getBraceSpacing());
        } else if (this.design.bracingType == 'custom') {
            html = 'Custom Bracing';
        } else if (this.design.bracingType == 'unbraced') {
            html = 'Unbraced';
        }
        return html
    }

    get shapeForAnalysis() {
        var shape = {};
        Object.keys(this.shape).forEach(function(key) {
            var value = this.shape[key];
            var newKey = key.replace("_over_", "/");
            shape[newKey] = value;
        }, this);

        if(this.isEuropeanSteel) {
            // consistent units: m and kg/m -> in and lb/ft
            shape["A"] *= 1550 // m^2 -> in^2
            shape["Ix"] *= 2402509.61 // m^4 -> in^4
            shape["Iy"] *= 2402509.61 // m^4 -> in^4
            shape["Sx"] *= 61023.7 // m^3 -> in^3
            shape["Sy"] *= 61023.7 // m^3 -> in^3
            shape["Zx"] *= 61023.7 // m^3 -> in^3
            shape["Zy"] *= 61023.7 // m^3 -> in^3
            shape["W"] *= 0.671968154 // kg/m -> lb/ft
        }

        if(this.isEngineeredLumber) {
            shape["A"] *= parseFloat(this.numberOfPlys);
            shape["Ix"] *= parseFloat(this.numberOfPlys);
            shape["W"] *= parseFloat(this.numberOfPlys);
            shape["Moment"] *= parseFloat(this.numberOfPlys);
            shape["Shear"] *= parseFloat(this.numberOfPlys);

            if(this.shape.flitch) {
                shape.A += this.transformedFlitchArea
                shape.Ix += this.transformedFlitchIx
                shape.Sx += this.transformedFlitchSx
                shape.W += this.flitchWeight
            }
        }

        if(this.isWood) {
            shape.A *= parseFloat(this.numberOfPlys);
            shape.Ix *= parseFloat(this.numberOfPlys);
            shape.Sx *= parseFloat(this.numberOfPlys);
            shape.W *= parseFloat(this.numberOfPlys);

            if(this.shape.flitch) {
                shape.A += this.transformedFlitchArea
                shape.Ix += this.transformedFlitchIx
                shape.Sx += this.transformedFlitchSx
                shape.W += this.flitchWeight
            }
        }

        return shape;
    }

    get materialForAnalysis() {
        if(this.material['Type'] === 'Visually Graded Lumber') {
            var gamma = 0.25; //1.1 slugs/ft^3/12in/12in*32.2
            this.shape["W"] = Number(gamma * Number(this.shape["A"]));
        }

        if(!("Zx" in this.shape) && !this.isEngineeredLumber) {
            this.shape["Zx"] = this.shape["Sx"];
        }

        if(!("W" in this.shape)) {
            this.shape["W"] = Number(Number(this.shape["A"]) * this.material["Density"] * 12);
        }

        var material = {};
        Object.keys(this.material).forEach(function(key) {
            var value = this.material[key];
            var newKey = key.replace("_over_", "/");
            material[newKey] = value;
        }, this);

        if(this.isEuropeanSteel) {
            material["E"] *= 145.03773773; // Gpa -> ksi
            material["Fu"] *= 0.145038; // MPa -> ksi
            material["Fy"] *= 0.145038; // MPa -> ksi
            material["Density"] *= 3.61273e-5; // kg/m^3 -> lb/in^3
        }

        if(this.isEngineeredLumber || this.isWood) {
            material["W"] *= parseFloat(this.numberOfPlys);
        }

        return material;
    }

    get projectForAnalysis() {
        if(this.material['Type'] === 'Visually Graded Lumber') {
            var gamma = 0.25; //1.1 slugs/ft^3/12in/12in*32.2
            this.shape["W"] = Number(gamma * Number(this.shape["A"]));
        }

        if(!("Zx" in this.shape) && !this.isEngineeredLumber) {
            this.shape["Zx"] = this.shape["Sx"];
        }

        if(!("W" in this.shape)) {
            this.shape["W"] = Number(Number(this.shape["A"]) * this.material["Density"] * 12);
        }

        if(this.design.includeSelfWeight){
            this.beam.selfWeight.x1 = 0;
            this.beam.selfWeight.x2 = this.beam.TotalLength()
            var plys = 1.0
            if(this.isEngineeredLumber || this.isWood) {
                plys = parseFloat(this.numberOfPlys)
            }
            this.beam.selfWeight.D = -Number(this.shape["W"]) / 12.0 * plys;
            if(this.shape.flitch) {
                this.beam.selfWeight.D -= this.flitchWeight / 12.0
            }
        } else {
            this.beam.selfWeight.D = 0;
        }

        if(this.shapeCanBeRotated === false) {
            this.beam.crossSectionRotation = 0;
        }

        var beamStr = JSON.stringify(this.beam);
        var shapeStr = JSON.stringify(this.shapeForAnalysis);
        var materialStr = JSON.stringify(this.materialForAnalysis);
        var designStr = JSON.stringify(this.design);

        return {
          beam: beamStr,
          shape: shapeStr,
          material: materialStr,
          design: designStr
        }
    }

    analyze() {
        
        //console.log("analyze called", new Error().stack);
        
        if(!BeamManager.checkStability(this)) {
            return;
        }

        document.dispatchEvent(new CustomEvent('analysis-started'))

        const projectDict = this.projectForAnalysis
        
        const designUrl = '/api/v1/beam-flexure/new-solver/design/';
        
        const url = designUrl
        fetch(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(projectDict),
        })
        .then(response => response.json())
        .then((results) => {
            document.dispatchEvent(new CustomEvent('analysis-completed', {detail: results}))
        })
        .catch((error) => {
            console.log(error)
            document.dispatchEvent(new CustomEvent('analysis-failed', {detail: 
                "There was a problem analyzing this beam. <a href='mailto:jeff@webstructural.com'>Report this problem.</a>"
            }))
        })
    }

    updateDesign() {
        this.analyze()
    }
}

/**
 * 1. Removes old and unused key / value pairs
 * 2. Ensure we have valid load cases for design
 * 3. Resets the load case exlude flag to false
 * @param {BeamProject} beamProject 
 */
function sanitizeAllLoadCaseTypes(beamProject) {
    loadCaseSanitizer(beamProject, 'loadCases')
    loadCaseSanitizer(beamProject, 'loadCombination')
    loadCaseSanitizer(beamProject, 'serviceCases')
}

/**
 * 1. Removes old and unused key / value pairs
 * 2. Ensure we have valid load cases for design
 * 3. Resets the load case exlude flag to false
 * @param {BeamProject} beamProject 
 * @param {String} loadCaseType : can be: 'loadCases', 'loadCombination' or 'serviceCases'
 */
function loadCaseSanitizer(beamProject, loadCaseType) {
    if(loadCaseType !== 'loadCases' && loadCaseType !== 'loadCombination' && loadCaseType !== 'serviceCases') {
        throw "Invalid load case type. Must be: 'loadCases', 'loadCombination' or 'serviceCases'"
    }
    const p = beamProject
    if(!(loadCaseType in p.design) || p.design[loadCaseType] === null) {
        switch(loadCaseType) {
            case 'loadCases':
                p.design[loadCaseType] = noCombinations
                break
            case 'loadCombination':
                p.design[loadCaseType] = noCombinations
                break
            case 'serviceCases':
                p.design[loadCaseType] = serviceCases
                break
        }
    } 
    
    if('prettyToUglyMap' in p.design[loadCaseType]) {
        delete p.design[loadCaseType]['prettyToUglyMap']
    }

    if('uglyToPrettyMap' in p.design[loadCaseType]) {
        delete p.design[loadCaseType]['uglyToPrettyMap']
    }

    if('bracing' in p.design[loadCaseType]) {
        delete p.design[loadCaseType]['bracing']
    }

    // JL NOTE: Bug fix 7/24/2022 (Mark Fiskin bug) and always set "exclude" to false - stop persisting this (it just causes problems)
    if('equations' in p.design[loadCaseType]) {
        for(let i = 0; i < p.design[loadCaseType].equations.length; i++) {
            // set exlude to false
            p.design[loadCaseType].equations[i].exclude = false
        }
    }
}
